import { useCallback, useRef, useState } from 'react'

import type { LockedAttachmentSource } from '../../types'

export interface UsePreviewToggleOptions {
  /** Gallery items carry their own per-item sources on the `gallery` prop already, so toggling skips the async fetch entirely. */
  isGallery: boolean
  /**
   * Lazily loads the underlying source; the resolved value is cached in
   * `source` and reused on subsequent toggles.
   */
  onFetchSource?: () => Promise<LockedAttachmentSource | void>
  /** Fired on every toggle tap, before any visibility/fetch logic runs. */
  onPreviewClick?: () => void
}

export interface UsePreviewToggleResult {
  source: LockedAttachmentSource | undefined
  isPreviewVisible: boolean
  isLoadingPreview: boolean
  handleToggle: () => Promise<void>
  /**
   * `handleToggle` when the card can actually be previewed (gallery, or a
   * fetch/click handler is wired), else `undefined` — pass straight to
   * `PreviewToggle.onToggle` so a non-previewable card renders no
   * toggle affordance.
   */
  togglePreview: (() => Promise<void>) | undefined
}

/**
 * Shared preview-toggle state for Composer/Media isMine: flips a locked thumbnail to
 * its unlocked preview, lazily fetching and caching the source on first
 * reveal. Media !isMine doesn't use this — it fetches on scroll-into-view/paid
 * instead of on a toggle tap.
 */
export function usePreviewToggle({
  isGallery,
  onFetchSource,
  onPreviewClick,
}: UsePreviewToggleOptions): UsePreviewToggleResult {
  const [source, setSource] = useState<LockedAttachmentSource | undefined>()
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const fetchingRef = useRef(false)

  const handleToggle = useCallback(async () => {
    onPreviewClick?.()
    if (isPreviewVisible) {
      setIsPreviewVisible(false)
      return
    }
    if (isGallery) {
      setIsPreviewVisible(true)
      return
    }
    if (source) {
      setIsPreviewVisible(true)
      return
    }
    if (!onFetchSource) return
    if (fetchingRef.current) return
    fetchingRef.current = true
    setIsLoadingPreview(true)
    try {
      const result = await onFetchSource()
      if (result) {
        setSource(result)
        setIsPreviewVisible(true)
      }
    } finally {
      fetchingRef.current = false
      setIsLoadingPreview(false)
    }
  }, [isPreviewVisible, isGallery, source, onPreviewClick, onFetchSource])

  const togglePreview =
    isGallery || onFetchSource != null || onPreviewClick != null
      ? handleToggle
      : undefined

  return {
    source,
    isPreviewVisible,
    isLoadingPreview,
    handleToggle,
    togglePreview,
  }
}
